import classNames from 'classnames';
import React from 'react';

import { AlmaLink } from '../alma-link';
import * as styles from './default-layout.module.css';

interface DefaultLayoutProps {
	abTestId: string;
	count: number;
	ctaAnalytics: any;
	ctaLink: string;
	elid: string;
	formattedModuleAbTestValue: string;
	providers: any[];
	stateName?: string;
}

export function DefaultLayout({
	abTestId,
	count,
	ctaAnalytics,
	ctaLink,
	elid,
	formattedModuleAbTestValue,
	providers,
	stateName,
}: DefaultLayoutProps) {
	return (
		<div className={styles.container}>
			<h2 className={classNames('heading-l', styles.header)}>
				Browse {count.toLocaleString()} available therapists{' '}
				{stateName ? 'in ' + stateName : ''}{' '}
			</h2>
			<div className={styles.link}>
				<AlmaLink
					cta={{
						abTestId,
						buttonColorTheme: 'dark',
						buttonSize: 'large',
						kind: 'button',
						link: ctaLink,
						title: 'Browse all therapists',
					}}
				/>
			</div>
			<div className={styles.providerGrid}>
				{providers?.map((provider, index) => {
					const acceptsInsurance =
						provider.acceptedInsuranceSlugs?.length > 0;
					const availability = provider.selfScheduleAvailability;
					const modalities = provider.filterables?.filter(
						(filterable) => filterable.slug?.includes('modality')
					);
					const offersVirtualCare = provider.filterables?.some(
						(filterable) =>
							filterable.slug === 'teletherapy_preference_online'
					);
					const profilePhoto = provider.profilePhoto;
					const providerId = provider.providerId;
					const providerFirstName = provider.providerFirstName;
					const providerLastName = provider.providerLastName;
					const providerSlug = provider.providerSlug;

					const providerCtaLink = `https://secure.helloalma.com/providers/${providerSlug}${formattedModuleAbTestValue ? `?ld_user_key=${elid}&ab=${formattedModuleAbTestValue}` : ''}`;

					// set header colors
					const headerColors = [
						styles.cardHeaderTeal,
						styles.cardHeaderGreen,
						styles.cardHeaderRust,
						styles.cardHeaderBlue,
					];
					const colorIndex = index % headerColors.length;

					return (
						<div key={providerId} className={styles.card}>
							<div
								className={classNames(
									styles.cardHeader,
									headerColors[colorIndex]
								)}
							>
								{availability && (
									<span
										className={classNames(
											'body-xs',
											styles.availabilityTag
										)}
									>
										Available{' '}
										{new Date(
											availability
										).toLocaleDateString('en-US', {
											weekday: 'long',
										})}
									</span>
								)}
							</div>
							<img
								src={profilePhoto}
								alt={`${providerFirstName} ${providerLastName}`}
								className={styles.image}
							/>
							<div className={styles.info}>
								<div>
									<h2
										className={classNames(
											'body-xl-bold',
											styles.name
										)}
									>{`${providerFirstName} ${providerLastName}`}</h2>
									<p
										className={classNames(
											'body-s',
											styles.modalities
										)}
									>
										{modalities?.map((modality, index) => (
											<span key={modality.slug || index}>
												{modality.name}
												{index < modalities.length - 1
													? ', '
													: ''}
											</span>
										))}
									</p>
								</div>
								<div className={styles.features}>
									{acceptsInsurance && (
										<span
											className={classNames(
												'body-xs-bold',
												styles.feature
											)}
										>
											<svg
												xmlns='http://www.w3.org/2000/svg'
												width='17'
												height='17'
												viewBox='0 0 17 17'
												fill='none'
											>
												<g clipPath='url(#clip0_107_945)'>
													<path
														d='M8.05469 0.736084L13.5326 1.9534C13.8377 2.02119 14.0547 2.29173 14.0547 2.6042V9.26202C14.0547 10.5994 13.3863 11.8484 12.2735 12.5902L8.05469 15.4028L3.83589 12.5902C2.72309 11.8484 2.05469 10.5994 2.05469 9.26202V2.6042C2.05469 2.29173 2.27171 2.02119 2.57673 1.9534L8.05469 0.736084ZM8.05469 2.10194L3.38802 3.13898V9.26202C3.38802 10.1536 3.83362 10.9862 4.57549 11.4808L8.05469 13.8003L11.5339 11.4808C12.2758 10.9862 12.7214 10.1536 12.7214 9.26202V3.13898L8.05469 2.10194ZM11.023 5.55064L11.9658 6.49344L7.72309 10.7361L4.89468 7.90768L5.83749 6.96482L7.72262 8.85002L11.023 5.55064Z'
														fill='#5B5855'
													/>
												</g>
												<defs>
													<clipPath id='clip0_107_945'>
														<rect
															width='16'
															height='16'
															fill='white'
															transform='translate(0.0546875 0.0693359)'
														/>
													</clipPath>
												</defs>
											</svg>
											Takes insurance
										</span>
									)}
									{offersVirtualCare && (
										<span
											className={classNames(
												'body-xs-bold',
												styles.feature
											)}
										>
											<svg
												xmlns='http://www.w3.org/2000/svg'
												width='17'
												height='17'
												viewBox='0 0 17 17'
												fill='none'
											>
												<path
													fillRule='evenodd'
													clipRule='evenodd'
													d='M1.38802 4.06942C1.38802 3.33304 1.98497 2.73608 2.72135 2.73608H10.7213C11.4577 2.73608 12.0547 3.33304 12.0547 4.06942V5.45125L13.6366 4.84864C14.1602 4.64916 14.7213 5.03589 14.7213 5.59623V10.5426C14.7213 11.1029 14.1602 11.4897 13.6366 11.2902L12.0547 10.6876V12.0694C12.0547 12.8058 11.4577 13.4028 10.7213 13.4028H2.72135C1.98497 13.4028 1.38802 12.8058 1.38802 12.0694V4.06942ZM10.7213 4.06942L2.72135 4.06942V12.0694H10.7213V8.75284L13.388 9.76871V6.37012L10.7213 7.386V4.06942Z'
													fill='#5B5855'
												/>
											</svg>
											Virtual sessions
										</span>
									)}
								</div>
								<div className={styles.divider} />
								<AlmaLink
									analytics={ctaAnalytics}
									cta={{
										abTestId,
										arrowPosition: 'right',
										hasArrow: true,
										kind: 'link',
										link: providerCtaLink,
										linkColor: 'cmat',
										openInNewTab: true,
										title: 'View profile',
									}}
								/>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}
